import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, CheckCircle2, ArrowRight, ArrowLeft, AlertTriangle,
  Lock, Eye, Server, Users, RefreshCw, FileWarning
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const essentials = [
  {
    title: 'SSL Certificate (HTTPS)',
    priority: 'Critical',
    icon: Lock,
    what: 'Encrypts data transmitted between your website and visitors.',
    why: 'Without SSL, browsers show "Not Secure" warnings that drive visitors away. Also required for SEO rankings.',
    how: [
      'Most hosting providers offer free SSL via Let\'s Encrypt',
      'Ensure your site redirects all HTTP traffic to HTTPS',
      'Check for mixed content warnings (HTTP resources on HTTPS pages)',
      'Renew before expiration - set calendar reminders',
    ]
  },
  {
    title: 'Regular Backups',
    priority: 'Critical',
    icon: RefreshCw,
    what: 'Copies of your website files and database stored separately from your hosting.',
    why: 'If your site gets hacked, crashes, or you make a mistake, backups let you restore quickly.',
    how: [
      'Set up daily automatic backups',
      'Store backups in a different location than your hosting (cloud storage)',
      'Test restoring from backup periodically',
      'Keep at least 30 days of backup history',
    ]
  },
  {
    title: 'Strong Password Policies',
    priority: 'Critical',
    icon: Users,
    what: 'Requirements for all admin accounts accessing your website.',
    why: 'Weak passwords are the #1 way hackers gain access to websites.',
    how: [
      'Minimum 12 characters with mixed case, numbers, and symbols',
      'Use a password manager (1Password, LastPass, Bitwarden)',
      'Enable two-factor authentication (2FA) on all admin accounts',
      'Never reuse passwords across different services',
    ]
  },
  {
    title: 'Software Updates',
    priority: 'High',
    icon: RefreshCw,
    what: 'Keeping your CMS, plugins, themes, and server software current.',
    why: 'Outdated software has known vulnerabilities that hackers actively exploit.',
    how: [
      'Enable automatic updates for minor releases',
      'Schedule monthly reviews of major updates',
      'Test updates on a staging site before applying to production',
      'Remove unused plugins and themes entirely',
    ]
  },
  {
    title: 'Web Application Firewall (WAF)',
    priority: 'High',
    icon: Shield,
    what: 'Filters malicious traffic before it reaches your website.',
    why: 'Blocks common attacks like SQL injection, cross-site scripting, and brute force attempts.',
    how: [
      'Cloudflare offers a free tier with basic WAF protection',
      'Sucuri and Wordfence are popular options for WordPress',
      'Configure rules based on your specific needs',
      'Monitor blocked requests to understand attack patterns',
    ]
  },
  {
    title: 'Security Monitoring',
    priority: 'High',
    icon: Eye,
    what: 'Tools that watch for suspicious activity and alert you to problems.',
    why: 'The sooner you know about a breach, the less damage it causes.',
    how: [
      'Set up uptime monitoring (UptimeRobot, Pingdom)',
      'Install malware scanning (Sucuri, Wordfence)',
      'Configure alerts for failed login attempts',
      'Monitor file changes on your server',
    ]
  },
  {
    title: 'Access Control',
    priority: 'Medium',
    icon: Users,
    what: 'Limiting who can do what on your website.',
    why: 'Not everyone needs admin access. Fewer privileged accounts means fewer attack vectors.',
    how: [
      'Use role-based permissions (Editor, Author, Contributor)',
      'Remove access immediately when team members leave',
      'Audit user accounts quarterly',
      'Use separate accounts for different people (no shared logins)',
    ]
  },
  {
    title: 'Secure Hosting',
    priority: 'Medium',
    icon: Server,
    what: 'A hosting provider with security best practices.',
    why: 'Your hosting environment is the foundation of your site\'s security.',
    how: [
      'Choose hosts with good security reputations',
      'Ensure they offer automated backups',
      'Look for DDoS protection',
      'Verify they keep server software updated',
    ]
  },
];

const commonThreats = [
  { threat: 'Brute Force Attacks', description: 'Automated attempts to guess your password', prevention: 'Strong passwords, 2FA, login attempt limits' },
  { threat: 'SQL Injection', description: 'Malicious code inserted through forms', prevention: 'WAF, input validation, parameterized queries' },
  { threat: 'Cross-Site Scripting (XSS)', description: 'Injecting malicious scripts into your pages', prevention: 'WAF, content security policies, output encoding' },
  { threat: 'Phishing', description: 'Fake emails tricking you into revealing credentials', prevention: 'Email authentication, team training, 2FA' },
  { threat: 'Malware', description: 'Malicious code uploaded to your site', prevention: 'Regular scanning, file integrity monitoring, updates' },
];

const GuideSecurityPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-security-page">
      <SEO 
        title="Website Security Essentials - Protect Your Business Online"
        description="Protect your business online with this guide to essential security measures every website needs. From SSL certificates to backup strategies."
        keywords="website security, SSL certificate, website backup, web application firewall, WAF, website protection, small business security, WordPress security"
        url="/resources/guides/security-essentials"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <Shield className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Security</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Website Security Essentials
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            Protect your business online with this guide to essential security measures every website needs. No technical expertise required.
          </p>
        </div>
      </section>

      {/* Warning Banner */}
      <section className="py-6 bg-red-50 border-y border-red-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-800 mb-1">Security is Not Optional</h3>
              <p className="text-red-700 text-sm">
                43% of cyber attacks target small businesses. The average cost of a data breach for small businesses is $120,000. Most breaches are preventable with basic security measures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Essentials */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8">Security Essentials Checklist</h2>
          
          <div className="space-y-6">
            {essentials.map((item, idx) => {
              const IconComponent = item.icon;
              return (
                <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl font-bold text-carbon-black">{item.title}</h3>
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            item.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                            item.priority === 'High' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {item.priority}
                          </span>
                        </div>
                        <p className="text-slate-gray"><strong>What:</strong> {item.what}</p>
                        <p className="text-slate-gray mt-1"><strong>Why:</strong> {item.why}</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-carbon-black mb-2">How to implement:</h4>
                      <ul className="space-y-2">
                        {item.how.map((step, stepIdx) => (
                          <li key={stepIdx} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-gray">{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Threats */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8">Common Threats to Know</h2>
          
          <div className="grid gap-4">
            {commonThreats.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-4">
                  <FileWarning className="h-5 w-5 text-amber-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-carbon-black">{item.threat}</h3>
                    <p className="text-sm text-slate-gray mb-2">{item.description}</p>
                    <p className="text-sm"><span className="font-medium text-emerald-600">Prevention:</span> {item.prevention}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need a Security Audit?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            We can assess your current security posture and implement the protections your business needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-emerald-400 text-emerald-600 hover:bg-emerald-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideSecurityPage;
